import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSetAtom, useAtomValue } from 'jotai';
import { Rating, Box, Stack, Tooltip, Typography, Fab } from '@mui/material';
import StarBorder from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import Information from '../../../PracticesPage/Components/Information';
import capitalize from '../../../../Helpers/StringsFormatting/capitalize';
import StatusChip from '../../../../Components/Chip/StatusChip';
import {
  compareDates,
  createNewDate,
  culture,
  options,
  addZero,
} from '../../../../Helpers/dates';
import {
  selectedLeadIdAtom,
  selectedCustomerIdAtom,
  userBranchesAtom,
  selectedBranchAtom,
  additionalCustomerDataAtom,
  allOutcomesAtom,
} from '../../../../Atoms';
import ConfirmPopup from '../../../../Components/Popup/ConfirmPopup';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded';

const classes = {
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '2%',
    alignItems: 'center',
    minHeight: '72px',
    maxHeight: '80px',

    padding: '12px 24px 12px 24px',
    maxWidth: 'unset',
    margin: 'unset',
  },
  icnWrap: {
    display: 'flex',
    alignItems: 'center',
  },
};

const CallStatusesOptions = {
  starting: (
    <Stack
      direction="column"
      spacing={2}
      sx={{
        justifyContent: "center",
        alignItems: "center"
      }}>
      <div className="dot-flashing" />
    </Stack>
  ),
  connected: PhoneInTalkRoundedIcon,
  disconnected: CallEndRoundedIcon,
  neverCalled: null,
  lastCallDate: null,
  neverCallAgain: DoNotDisturbAltIcon,
  answeredCall: DoneIcon,
  notAnsweredCall: CloseIcon,
};

const statusesColors = {
  connected: '#1D5B9D',
  disconnected: '#fca600',
  neverCalled: '#BCBCBC',
  lastCallDate: '#c6cbf0',
  neverCallAgain: '#5AC4FF',
  answeredCall: '#34A853',
  notAnsweredCall: '#b00020',
};

const LeadsListItem = function LeadsListItem({ data, handleLeadToCustomer }) {
  const intl = useIntl();

  const allOutcomes = useAtomValue(allOutcomesAtom);
  const userBranches = useAtomValue(userBranchesAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const setSelectedLeadId = useSetAtom(selectedLeadIdAtom);
  const setSelectedCustomerId = useSetAtom(selectedCustomerIdAtom);
  const setAdditionalCustomerData = useSetAtom(additionalCustomerDataAtom);
  const [confirmAccept, setConfirmAccept] = useState(false);

  const handleSelected = () => {
    if (data.customerId) {
      setSelectedCustomerId(data.customerId);
      setAdditionalCustomerData({
        customerName: data.fullName,
      });
    } else {
      setSelectedLeadId(data.id);
      setAdditionalCustomerData({
        customerName: data.fullName,
      });
    }
  };

  const openConfirmPopup = (event) => {
    event.stopPropagation();
    setConfirmAccept(event.currentTarget);
  };
  const callStatus = useMemo(() => {
    if (data?.lastCallStatus && !data?.lastCallOutcome)
      return data?.lastCallStatus?.toLowerCase();

    if (!data?.lastCallCompletedOn) return 'neverCalled';

    if (data?.lastCallOutcome === 10) return 'neverCallAgain';

    if (compareDates(new Date(data?.lastCallCompletedOn), new Date())) {
      const lastOutcome = allOutcomes.find(
        (o) => data?.lastCallOutcome === o.id
      );
      return lastOutcome?.extensionData?.answered
        ? 'answeredCall'
        : 'notAnsweredCall';
    }
    return 'lastCallDate';
  }, [data]);

  return (
    <>
      <div
        id="data-entry"
        style={classes.root}
        aria-hidden="true"
        onClick={handleSelected}
      >
        <Information
          inverted
          caption={
            <div style={classes.icnWrap}>
              <Rating
                name="read-only"
                value={parseInt(data.ratingValue, 10) || 0}
                precision={1}
                readOnly
                emptyIcon={
                  <StarBorder
                    fontSize="inherit"
                    sx={{ color: 'primary.main' }}
                  />
                }
                icon={
                  <StarIcon fontSize="inherit" sx={{ color: 'primary.main' }} />
                }
              />
              <Typography
                variant="body1"
                sx={{ ml: '1%', fontSize: 'inherit' }}
                noWrap
              >
                {parseInt(data.ratingValue, 10) || '0'}
              </Typography>
            </div>
          }
          value={capitalize(data.fullName)}
        />
        <Information
          caption={intl.messages.MOBILE_PHONE}
          value={
            userBranches.length > 1 &&
            selectedBranch &&
            data.mobilePhoneNumber ? (
              <a
                style={{
                  color: 'inherit',
                  fontFamily: 'Roboto',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  letterSpacing: '0.02em',
                }}
                href={`tel:${selectedBranch.prefix3CX}${data.mobilePhoneNumber.replace('+39', '')}`}
              >
                {data.mobilePhoneNumber}
              </a>
            ) : (
              data.mobilePhoneNumber
            )
          }
        />
        <Information
          caption={intl.messages.FISCALCODE}
          value={data.fiscalCode}
        />
        <Information
          caption={intl.messages.BIRTH_DATE}
          value={
            data.birthDate
              ? `${createNewDate(data.birthDate).toLocaleDateString(culture, options)}`
              : null
          }
        />
        <Information
          caption={intl.messages.LEAD_ORIGIN}
          value={capitalize(data.leadOrigin)}
        />

        {!data.customerId ? (
          <Tooltip
            title={intl.messages.LEAD_TO_CUSTOMER}
            placement="bottom"
            sx={{ boxShadow: 'unset' }}
          >
            <Fab color="primary" size="medium" onClick={openConfirmPopup}>
              <RecentActorsIcon />
            </Fab>
          </Tooltip>
        ) : (
          <Box sx={{
            width: "48px"
          }} />
        )}
        <Box
          sx={{
            maxWidth: "130px",
            minWidth: "130px",
            display: "flex",
            justifyContent: "center",
            alignItems: "stretch"
          }}>
          {data.lastCallOutcome === 0 ? (
            <Stack
              direction="column"
              spacing={2}
              sx={{
                justifyContent: "center",
                alignItems: "center"
              }}>
              <div className="dot-flashing" />
            </Stack>
          ) : (
            <Tooltip
              title={
                data.lastCallCreatedByFullName
                  ? `${intl.messages.PERFORMED_BY} ${data.lastCallCreatedByFullName} - ${addZero(
                      createNewDate(data.lastCallCompletedOn)
                    )}`
                  : capitalize(intl.messages[callStatus?.toUpperCase()])
              }
              placement="left"
              arrow
            >
              <StatusChip
                alternativeLabel={
                  callStatus === 'lastCallDate'
                    ? `${createNewDate(
                        data?.lastCallCompletedOn
                      ).toLocaleDateString(culture, {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}`
                    : null
                }
                status={callStatus}
                options={CallStatusesOptions}
                colors={statusesColors}
              />
            </Tooltip>
          )}
        </Box>
      </div>
      <ConfirmPopup
        confirmText={intl.messages.CONFIRM_LEAD_TO_CUSTOMER}
        open={Boolean(confirmAccept)}
        anchorEl={confirmAccept}
        close={() => setConfirmAccept(null)}
        handleConfirm={() => handleLeadToCustomer(data.id)}
      />
    </>
  );
};

LeadsListItem.propTypes = {
  data: PropTypes.object,
  handleLeadToCustomer: PropTypes.func,
};

export default LeadsListItem;
